import React, { useEffect, useRef } from 'react';
import { Participant } from 'livekit-client';
import ParticipantThumbnail from './ParticipantThumbnail';
import { cn } from '../lib/utils';

interface ThumbnailStripProps {
    participants: Participant[];
    localParticipant: Participant | null;
    activeParticipant: Participant | null;
    onThumbnailClick: (participant: Participant) => void;
    position?: 'top' | 'bottom' | 'right';
}

const ThumbnailStrip: React.FC<ThumbnailStripProps> = ({
    participants,
    localParticipant,
    activeParticipant,
    onThumbnailClick,
    position = 'bottom',
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to active thumbnail
    useEffect(() => {
        if (!activeParticipant || !scrollContainerRef.current) return;

        const container = scrollContainerRef.current;
        const activeIndex = [localParticipant, ...participants].findIndex(
            (p) => p?.identity === activeParticipant.identity
        );

        if (activeIndex !== -1) {
            const isVertical = position === 'right';

            if (isVertical) {
                const thumbnailHeight = 105; // Height + gap
                const scrollPosition = activeIndex * thumbnailHeight - container.clientHeight / 2 + thumbnailHeight / 2;

                container.scrollTo({
                    top: Math.max(0, scrollPosition),
                    behavior: 'smooth',
                });
            } else {
                const thumbnailWidth = 140; // Width + gap
                const scrollPosition = activeIndex * thumbnailWidth - container.clientWidth / 2 + thumbnailWidth / 2;

                container.scrollTo({
                    left: Math.max(0, scrollPosition),
                    behavior: 'smooth',
                });
            }
        }
    }, [activeParticipant, participants, localParticipant, position]);

    const allParticipants = localParticipant ? [localParticipant, ...participants] : participants;

    // Show all participants in thumbnails, including the focused one
    const thumbnailParticipants = allParticipants;

    if (thumbnailParticipants.length === 0) return null;

    const isVertical = position === 'right';

    return (
        <div
            className={cn(
                'bg-card/80 backdrop-blur-sm border-border',
                isVertical
                    ? 'h-full w-auto border-l flex flex-col py-4 px-3 overflow-hidden'
                    : cn(
                        'w-full py-3 px-4',
                        position === 'bottom' ? 'border-t' : 'border-b'
                    )
            )}
        >
            <div
                ref={scrollContainerRef}
                className={cn(
                    'flex gap-3 scroll-smooth',
                    isVertical
                        ? 'flex-col overflow-y-auto overflow-x-hidden flex-1 min-h-0 scrollbar-thin px-2 py-2'
                        : 'overflow-x-auto overflow-y-hidden py-2 px-2 scrollbar-thin'
                )}
                style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'hsl(var(--primary) / 0.5) transparent',
                }}
            >
                {thumbnailParticipants.map((participant) => (
                    <ParticipantThumbnail
                        key={participant.identity}
                        participant={participant}
                        isLocal={participant.identity === localParticipant?.identity}
                        isActive={participant.identity === activeParticipant?.identity}
                        onClick={() => onThumbnailClick(participant)}
                    />
                ))}
            </div>
        </div>
    );
};

export default ThumbnailStrip;
